/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

import Layer from "App/render/webgl/layers/layer";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import {Containers} from "App/utils/factory";
import WebGLContext from "App/web_gl_context";
import {Framebuffer} from "App/utils/frame_buffer";
import View from "App/view/view";
import AOMRT from "App/render/webgl/passes/ssao/ao_mrt";
import AOGeometry from "App/render/webgl/passes/ssao/ao_geometry";
import AONormals from "App/render/webgl/passes/ssao/ao_normals";
import AO from "App/render/webgl/passes/ssao/ao";
import AOBlur from "App/render/webgl/passes/ssao/ao_blur";

export default
class SSAOLayer extends Layer {
    private _texture: WebGLTexture;

    constructor(aRenderGraph: WebGLRenderGraph) {
        super(aRenderGraph);

        this._shadersID = Containers.SHADERS_SSAO;
        this._passesID = Containers.PASSES_SSAO;
        this._name = 'SSAO';

        /** @type WebGLTexture */
        this._texture = null;      // Texture where is AO rendered
    }

    /**
     * @return {WebGLTexture}
     */
    public getTexture(): WebGLTexture {
        return this._texture;
    }

    /**
     * @override
     */
    public invalidateFramebuffers() {
        if (!this._fboReady) {
            return;
        }

        super.invalidateFramebuffers();
        this._texture = null;
    }

    /**
     * @override
     */
    public invalidateShaders() {
        if (!this._shadersReady) {
            return;
        }

        super.invalidateShaders();
        this._renderGraph.initializeShaders();
    }

    /**
     * @override
     */
    public initialize() {
        if (this._fboReady && this._shadersReady && this._passesReady) {
            return;
        }

        super.initialize();

        // Recreate shaders in RenderGraph
        this._renderGraph.initializeShaders();
    }

    /**
     * @override
     */
    protected _createFramebuffers() {
        let gl = this._context.getWebGLRenderingContext();
        let scale = 1.0;
        let contentSize = this._context.getCanvas().getContentSize();

        // SSAO a SSAOBlur uses Float2Vec4 to reduce their sizes
        // Try to use float16, if not, use float32
        let floatType = WebGLContext.caps.FLOAT16;
        if (!floatType) {
            floatType = WebGLContext.caps.FLOAT32;
        }

        // Use MRT
        if (this._sceneAPI.lightingModule.getSSAO().getUseMRT()) {
            this._framebuffers.add('ssao_mrt',
                new Framebuffer(this._renderGraph, {
                    mrt: true,
                    scale: scale,
                    size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                    createDepthRenderBuffer: true,
                    colorTexture: [{
                        filtering: gl.NEAREST,
                        dataType: floatType
                    }, {
                        filtering: gl.NEAREST,
                        dataType: floatType
                    }
                    ]
                })
            );
        } else {
            this._framebuffers.add('geometry',
                new Framebuffer(this._renderGraph, {
                    scale: scale,
                    size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                    colorTextureFiltering: gl.NEAREST,
                    colorTexturePixelDataType: floatType,
                    createColorTexture: true,
                    createDepthTexture: false,
                    createDepthRenderBuffer: true
                })
            );

            this._framebuffers.add('normals',
                new Framebuffer(this._renderGraph, {
                    scale: scale,
                    size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                    colorTextureFiltering: gl.NEAREST,
                    colorTexturePixelDataType: floatType,
                    createColorTexture: true,
                    createDepthTexture: false,
                    createDepthRenderBuffer: true
                })
            );
        }

        this._framebuffers.add('ssao',
            new Framebuffer(this._renderGraph, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: false
            })
        );

        this._framebuffers.add('ssaoWithBlur',
            new Framebuffer(this._renderGraph, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: false
            })
        );
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @override
     */
    public render(aView: View): boolean {
        let ssao = this._sceneAPI.lightingModule.getSSAO();
        if (!ssao.isEnabled()) {
            this._texture = null;
            return true;
        }

        if (!this.readyToRender()) {
            return true;
        }

        // MRT
        if (ssao.getUseMRT()) {
            return this._renderMRT(aView);
        }
        // Pass by Pass
        else {
            return this._renderClassic(aView);
        }
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @private
     */
    private _renderMRT(aView: View) {
        let mrtBuffer = this._framebuffers.get("ssao_mrt");

        // 1,2 - Render positions and normals
        this._passes.get(AOMRT.TypeName).render(
            aView,
            mrtBuffer
        );

        // Set variables
        let positionTexture = mrtBuffer.renderable.getColorTexture(0);
        let normalsTexture = mrtBuffer.renderable.getColorTexture(1);

        return this._renderRest(aView, positionTexture, normalsTexture);
    }

    /**
     * @param {View} aView
     * @return {boolean}
     * @private
     */
    private _renderClassic(aView: View): boolean {
        let geometryBuffer = this._framebuffers.get("geometry");
        let normalBuffer = this._framebuffers.get("normals");

        // Debug
        let debugBuffer = this._sceneAPI.lightingModule.getSSAO().getDebugBuffer();
        switch (debugBuffer) {
            case 1:
                geometryBuffer = this._framebuffers.get("screen");
                break;
            case 2:
                normalBuffer = this._framebuffers.get("screen");
                break;
        }

        // 1 - Render positions
        this._passes.get(AOGeometry.TypeName).render(
            aView,
            geometryBuffer
        );

        if (debugBuffer === 1) {
            return false;
        }

        // 2 - Render normals
        this._passes.get(AONormals.TypeName).render(
            aView,
            normalBuffer
        );

        if (debugBuffer === 2) {
            return false;
        }

        // Set variables
        let positionTexture = geometryBuffer.renderable.getColorTexture();
        let normalsTexture = normalBuffer.renderable.getColorTexture();

        return this._renderRest(aView, positionTexture, normalsTexture);
    }

    /**
     * @param {View} aView
     * @param {WebGLTexture} aPositionTexture
     * @param {WebGLTexture} aNormalsTexture
     * @return {boolean}
     * @private
     */
    private _renderRest(aView: View, aPositionTexture: WebGLTexture, aNormalsTexture: WebGLTexture): boolean {
        let ssaoBuffer = this._framebuffers.get("ssao");
        let blurBuffer = this._framebuffers.get("ssaoWithBlur");

        // Debug
        let debugBuffer = this._sceneAPI.lightingModule.getSSAO().getDebugBuffer();
        switch (debugBuffer) {
            case 3:
                ssaoBuffer = this._renderGraph.getFramebuffersContainer().get("screen");
                break;
            case 4:
                blurBuffer = this._renderGraph.getFramebuffersContainer().get("screen");
                break;
        }

        // 3 - Calculate SSAO Effect
        this._passes.get(AO.TypeName).render(
            aView,
            ssaoBuffer,
            aPositionTexture,
            aNormalsTexture
        );

        if (debugBuffer === 3) {
            return false;
        }

        // 4 - Blur SSAO Effect
        this._passes.get(AOBlur.TypeName).render(
            aView,
            blurBuffer,
            ssaoBuffer.renderable.getColorTexture()
        );

        if (debugBuffer === 4) {
            return false;
        }

        this._texture = blurBuffer.renderable.getColorTexture();
        return true;
    }
}
